import React, { useEffect, useState, useCallback } from "react";

/**
 * Toast Notification Component
 * 
 * Usage:
 * <Notification 
 *   message="Settings saved successfully!"
 *   type="success" // success | error | warning | info
 *   show={showNotification}
 *   onClose={() => setShowNotification(false)}
 *   duration={3000} // optional, auto-dismiss after ms (default: 3000)
 * />
 */

const Notification = ({ 
  message, 
  type = "success", 
  show = false, 
  onClose, 
  duration = 3000 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isEntering, setIsEntering] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  const handleClose = useCallback(() => {
    setIsLeaving(true);
    setIsEntering(false);
    setTimeout(() => {
      setIsVisible(false);
      setIsLeaving(false);
      if (onClose) onClose();
    }, 300); // Match animation duration
  }, [onClose]);

  useEffect(() => {
    if (show) {
      // First set visible, then trigger enter animation
      setIsVisible(true);
      setIsLeaving(false);
      setIsEntering(false);
      // Small delay to ensure DOM is ready, then trigger enter animation
      const enterTimer = setTimeout(() => {
        setIsEntering(true);
      }, 10);
      
      // Auto-dismiss after duration
      const dismissTimer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => {
        clearTimeout(enterTimer);
        clearTimeout(dismissTimer);
      };
    } else if (isVisible) {
      // If show becomes false while visible, trigger leave animation
      handleClose();
    }
  }, [show, duration, handleClose, isVisible]);

  if (!isVisible) return null;

  // Icon and colors based on type
  const config = {
    success: {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-800",
      icon: (
        <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
        </svg>
      ),
    },
    error: {
      bg: "bg-red-50",
      border: "border-red-200",
      text: "text-red-800",
      icon: (
        <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
        </svg>
      ),
    },
    warning: {
      bg: "bg-amber-50",
      border: "border-amber-200",
      text: "text-amber-800",
      icon: (
        <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      ),
    },
    info: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-800",
      icon: (
        <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
        </svg>
      ),
    },
  };

  const currentConfig = config[type] || config.info;

  return (
    <div 
      className={`fixed top-4 right-4 z-50 max-w-sm transform transition-all duration-300 ease-out ${
        isLeaving || !isEntering
          ? "translate-x-full opacity-0 scale-95" 
          : "translate-x-0 opacity-100 scale-100"
      }`}
    >
      <div 
        className={`flex items-start gap-3 rounded-lg border p-4 shadow-lg ${currentConfig.bg} ${currentConfig.border}`}
      >
        <div className="flex-shrink-0">
          {currentConfig.icon}
        </div>
        <div className={`flex-1 text-sm font-medium ${currentConfig.text}`}>
          {message}
        </div>
        <button
          type="button"
          onClick={handleClose}
          className={`flex-shrink-0 rounded-md p-1 transition-colors hover:bg-black/10 ${currentConfig.text}`}
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Notification;
